import { t } from "components/CentralDataMangement/translation/Translation";
import CheckBoxField from "components/common/atoms/CheckBoxField";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import React, { useEffect, useState } from "react";
import {
  initialFormData,
  jobApplicationProps,
  applicationOptions,
} from "./JobApplicationsInterface";
import Button from "components/common/atoms/Button";
import ModalPopup from "components/common/atoms/ModalPopup";
import { ValidationRules } from "components/common/utlis/TypeAnnotations";
import LabelField from "components/common/atoms/LabelField";
import InputTextfield from "components/common/atoms/InputTextField";
import ViewIcon from "static/images/ViewIcon";
import GetCrudPermissions from "components/common/services/RolesAndPermissions/GetCrudPermissions";
import LabelWithTextAreaField from "components/common/molecules/LabelWithTextAreaField";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import {
  validateForm,
  validateSelectField,
} from "components/common/services/ValidationService";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiCall } from "components/common/services/ApiServices";
import * as ENDPOINTS from "../../../routes/ApiEndpoints";
import CustomNotify from "components/common/atoms/CustomNotify";
import { Option } from "components/common/utlis/TypeAnnotations";
import { CANDIDATE_MICROSERVICE, CENTRAL_DATA_MANAGEMENT_MICROSERVICE } from "Constants";
import { selectAuth } from "features/auth/AuthSlice";
import { useSelector } from "react-redux";
import Title from "components/common/atoms/Title";
import ResumeIcon from "static/images/ResumeIcon";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import { PHONE_CALL_URL } from "routes/ApiEndpoints";
import { formatPhoneFor3CX } from "components/common/utlis/HelperFunctions";

const ViewJobApplicationOverView: React.FC = () => {
  const [formData, setFormData] =
    useState<jobApplicationProps>(initialFormData);

  // Inflow-channel dropdown options (same list as candidate Internal Info), served by getJobApplicationDetails.
  const [inflowChannelList, setInflowChannelList] = useState<Option[]>([]);

  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const userAuth = useSelector(selectAuth);
  const canCompany = GetCrudPermissions("Company");

  // Fetch job application details
  const fetchJobApplicationDetails = async () => {
    const url = applicationId
      ? `${ENDPOINTS.GET_JOB_APPLICATION_DETAILS}/${applicationId}`
      : ENDPOINTS.GET_JOB_APPLICATION_DETAILS;
    const response = await ApiCall.getService(
      url,
      "GET",
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
      true
    );
    if (response?.status === 200) {
      setFormData(response?.data);
      setInflowChannelList(response?.data?.inflowChannelList || []);
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured"),
      });
    }
  };

  useEffect(() => {
    fetchJobApplicationDetails();
  }, [applicationId]);

  const initialErrors: { [key: string]: string } = {};
  const [errors, setErrors] = useState<{ [key: string]: string }>(
    initialErrors
  );
  const [modal, setModal] = useState(false);

  const handleSelectChange = (selectedOption: any, name: string) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: selectedOption,
    }));
    validation(name, selectedOption, true);
  };

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const [loading, setLoading] = useState(false);

  const validation = (
    name: string,
    value: string | boolean | Option | null | number,
    isSingleFieldValidation: boolean = false
  ) => {
    const validationRules: ValidationRules = {
      status: [validateSelectField],
      inflowChannel: [validateSelectField],
    };

    const validationErrors = validateForm(
      { ...formData, [name]: value },
      validationRules,
      isSingleFieldValidation ? name : undefined
    );

    if (isSingleFieldValidation && Object.keys(errors).length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name],
      }));
    } else {
      setErrors(validationErrors);
    }

    if (Object.keys(validationErrors).length > 0) {
      return false;
    }

    return true;
  };

  const handleCreateCandidate = async () => {
    setModal(false);
    // Save first so the recruiter-selected inflow channel is persisted; the candidate
    // creation form then prefills its inflow channel from the stored id. If save fails
    // (e.g. required fields missing) we stay on the page so it can be corrected.
    const response = await saveApplication();
    if (!response) {
      return;
    }
    const appStatus = formData.status?.value ?? 1;
    navigate(`/candidate/create?tabId=general&destination=/manage/job-applications&&applicationId=${applicationId}&&appStatus=${appStatus}`);
  };

  const isModalOpen = () => {
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
  };

  const navigateToMatching = async (candidateId: number | null) => {

    const data = {
      candidateId: candidateId,
      function: formData.jobOffer
    }
    const response = await ApiCall.service(
      ENDPOINTS.ADD_FUNCTION_TO_CANDIDATE,
      "POST",
      data,
      false,
      CANDIDATE_MICROSERVICE
    );

    navigate(`/matching-list/candidate/${candidateId}`);
  };

  // Persist the application's editable fields (status, references, channel, inflow channel).
  // Shared by the Save button and by Create candidate, so the selected inflow channel is stored
  // before conversion prefills the candidate from it. Returns the response on success, else null.
  const saveApplication = async () => {
    if (!validation("status", formData.status)) {
      return null;
    }
    setLoading(true);
    const data = {
      status: formData.status,
      candidateId: formData.candidateId,
      externalRef: formData.externalRef,
      externalInfo: formData.externalInfo,
      channel: formData.channel,
      inflowChannel: formData.inflowChannel,
    };
    const url = !applicationId
      ? ENDPOINTS.UPDATE_OR_CREATE_JOB_APPLICATION
      : `${ENDPOINTS.UPDATE_OR_CREATE_JOB_APPLICATION}/${applicationId}`;
    const response = await ApiCall.service(
      url,
      "POST",
      data,
      false,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );
    setLoading(false);
    if (response?.status === 200) {
      await fetchJobApplicationDetails();
      return response;
    }
    CustomNotify({
      type: "error",
      message: t("Some error occured"),
    });
    return null;
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    const response = await saveApplication();
    if (response) {
      CustomNotify({ type: "success", message: t(response?.msg) });
    }
  };

  // CR: read-only field whose label carries a "view details" icon that opens the
  // related record (candidate/company/vacancy) in a new tab. Icon only shows when `to` is set.
  const renderViewLinkedField = (
    label: string,
    value: string,
    id: string,
    to: string | null
  ) => (
    <>
      <div className="d-flex align-items-center">
        <LabelField title={t(label)} className="form-label field-label me-2" />
        {to && (
          <Link
            to={to}
            target="_blank"
            rel="noreferrer"
            title={t("View details")}
            className="table-action-icons"
            style={{ marginBottom: "0.3vw" }}
          >
            <ViewIcon />
          </Link>
        )}
      </div>
      <InputTextfield
        id={id}
        type="text"
        value={value}
        isDisabled={true}
        placeholder={t(label)}
        handleChange={() => {}}
        name={id}
      />
    </>
  );

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Job application",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={t("Applications")} />
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-12">
            <div className="form-border">
              <div className="row">
                <div className="col-sm-12 col-xxl-4 col-md-4">
                  <SelectWithSearch
                    title={t("Status")}
                    search={true}
                    options={applicationOptions}
                    onChange={(e) => handleSelectChange(e, "status")}
                    isMulti={false}
                    name="status"
                    value={formData.status}
                    isDisabled={false}
                    error={errors.status}
                    isTranslate={true}
                  />
                </div>
                <div className="col-md-8 mt-35">
                  <div className="d-flex justify-content-end align-items-center">
                    {formData.candidateId === null && (
                      <Button
                        title={t("Create candidate")}
                        handleClick={isModalOpen}
                        className="form-button shadow-none me-2"
                      />
                    )}
                    {formData.candidateId !== null && (
                      <Button
                        title={t("Matching")}
                        handleClick={() =>
                          navigateToMatching(formData.candidateId)
                        }
                        className="form-button shadow-none me-2"
                      />
                    )}
                    {/* CR: Save button moved here (top, to the right of Create candidate/Matching) */}
                    {!loading ? (
                      <Button
                        title={t("Save")}
                        type="submit"
                        className="form-button shadow-none"
                      />
                    ) : (
                      <LoadingIcon
                        iconType="bars"
                        color="#e55496"
                        className="d-inline-block"
                        width={"3vw"}
                        height={"3vw"}
                      />
                    )}
                  </div>
                </div>

                <ModalPopup
                  size="xl"
                  show={modal}
                  onHide={closeModal}
                  title={t("Create candidate")}
                  body={
                    t(
                      "You are about to create candidate based on the information from this application. Do you wish to continue"
                    ) + "?"
                  }
                  className="modal-lg jobApplicationModal"
                  onConfirmButtonClick={handleCreateCandidate}
                  confirmTitle={t("Yes")}
                />
              </div>
              <div className="row">
                <div className="col-sm-12 col-xxl-3 col-md-3">
                  <LabelWithInputField
                    isMandatory={false}
                    name="dateOfApplication"
                    handleChange={() => { }}
                    value={formData.dateOfApplication}
                    isDisabled={true}
                    id="dateOfApplication"
                    label={t("Date of Application")}
                    type="text"
                    placeholder={t("Date of Application")}
                  />
                </div>
                {/* Raw channel received from the external source: read-only, and hidden when empty. */}
                {formData.channel && (
                  <div className="col-sm-12 col-xxl-3 col-md-3">
                    <LabelWithInputField
                      isMandatory={false}
                      name="channel"
                      handleChange={handleFieldChange}
                      value={formData.channel}
                      isDisabled={true}
                      id="channel"
                      label={t("Channel")}
                      type="text"
                      placeholder={t("Channel")}
                    />
                  </div>
                )}
                {/* Recruiter-selected inflow channel: same option list as the candidate Internal Info
                    tab; mandatory, and carried onto the candidate on conversion. */}
                <div className="col-sm-12 col-xxl-3 col-md-3">
                  <SelectWithSearch
                    title={t("Inflow channel")}
                    search={true}
                    options={inflowChannelList}
                    onChange={(e) => handleSelectChange(e, "inflowChannel")}
                    isMulti={false}
                    name="inflowChannel"
                    value={formData.inflowChannel}
                    isDisabled={false}
                    error={errors.inflowChannel}
                    isMandatory={true}
                    isTranslate={true}
                  />
                </div>
                <div className="col-sm-12 col-xxl-3 col-md-3">
                  {renderViewLinkedField(
                    "Candidate",
                    formData.candidate,
                    "candidate",
                    formData.candidateId
                      ? `/candidate/corner/${formData.candidateId}`
                      : null
                  )}
                </div>
                <div className="col-sm-12 col-xxl-3 col-md-3">
                  <SelectWithSearch
                    title={t("Office")}
                    search={true}
                    options={[]}
                    onChange={() => { }}
                    isMulti={false}
                    name="office"
                    value={formData.office}
                    isDisabled={true}
                    isTranslate={true}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12 col-xxl-3 col-md-3">
                  <LabelWithInputField
                    isMandatory={false}
                    name="jobOffer"
                    handleChange={() => { }}
                    value={formData.jobOffer}
                    isDisabled={true}
                    id="jobOffer"
                    label={t("Job offer")}
                    type="text"
                    placeholder={t("Job offer")}
                  />
                </div>
                <div className="col-sm-12 col-xxl-3 col-md-3">
                  <LabelWithInputField
                    isMandatory={false}
                    name="viewApplicationStatus"
                    handleChange={() => { }}
                    value={formData.viewApplicationStatus}
                    isDisabled={true}
                    id="viewApplicationStatus"
                    label={t("Application Status")}
                    type="text"
                    placeholder={t("Application Status")}
                  />
                </div>
                <div className="col-sm-12 col-xxl-3 col-md-3 mt-35 pt-2">
                  <CheckBoxField
                    label={t("Spontaneous")}
                    name="spontaneous"
                    onChangeHandler={() => { }}
                    isChecked={formData.spontaneous === 1}
                    className="me-5"
                    disable={true}
                    id="spontaneous"
                    lineHeight="1vw"
                  />
                  <CheckBoxField
                    label={t("Sponsered")}
                    name="sponsered"
                    onChangeHandler={() => { }}
                    isChecked={formData.sponsered === 1}
                    disable={true}
                    id="sponsered"
                    lineHeight="1vw"
                  />
                </div>
                <div className="col-sm-12 col-xxl-3 col-md-3">
                  {renderViewLinkedField(
                    "Company name",
                    formData.companyName,
                    "companyName",
                    canCompany?.read &&
                      canCompany?.update &&
                      formData.companyId
                      ? `/company/corner/${formData.companyId}`
                      : null
                  )}
                </div>
              </div>
              <div className="row marginBotttom1">
                <div className="col-12">
                  <LabelField
                    title={t("Contact details")}
                    className="tab-subtitle"
                  />
                </div>
                <div className="col-sm-12 col-md-4 mb-3">
                  <LabelField
                    title={t("Mobile") + ":"}
                    key="PhoneInput"
                    className="marginRightPoint5 fw-bold"
                  />
                  <a
                    href={
                      formData.mobNumber !== null &&
                        formData.mobNumber !== "" &&
                        formData.mobNumber !== undefined
                        ? `${PHONE_CALL_URL}${formatPhoneFor3CX(
                          formData.mobNumber
                        ).replace("+", "%2B")}`
                        : ""
                    }
                    target="_blank"
                    className="link-color"
                  >
                    {formData.mobNumber}
                  </a>
                </div>
                <div className="col-sm-12 col-xxl-4 col-md-4">
                  <LabelField
                    title={t("Email") + ":"}
                    key="Email"
                    className="marginRightPoint5 fw-bold"
                  />
                  <a
                    href={`mailto:${formData.email}`}
                    target="_blank"
                    className="link-color"
                  >
                    {formData.email}
                  </a>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  {/* CR: "Address" section header hidden — its fields are commented below
                      and the resume now shows as an inline "Candidate resume :" row in the
                      body. Old label kept commented so it can be re-enabled later. */}
                  {/* <LabelField title={t("Address")} className="tab-subtitle" /> */}
                </div>
                <div className="col-6">
                  <LabelField
                    title={t("Vacancy information")}
                    className="tab-subtitle"
                  />
                </div>
                <div className="col-6 ps-1">
                  <div className="row">
                    {/* CR: address block (street/number/box/zip/city/country),
                        Motivation and External reference are hidden for now.
                        Kept in code (commented) so they can be re-enabled later. */}
                    {/*
                    <div className="col-sm-12 col-md-4">
                      <LabelWithInputField
                        isMandatory={false}
                        name="street"
                        handleChange={() => { }}
                        value={formData.street}
                        id="street"
                        label={t("Street")}
                        type="text"
                        isDisabled={true}
                      />
                    </div>

                    <div className="col-sm-12 col-md-4">
                      <LabelWithInputField
                        isMandatory={false}
                        name="number"
                        handleChange={() => { }}
                        value={formData.number}
                        id="number"
                        label={t("Number")}
                        type="text"
                        isDisabled={true}
                      />
                    </div>
                    <div className="col-sm-12 col-md-4">
                      <LabelWithInputField
                        isMandatory={false}
                        name="box"
                        handleChange={() => { }}
                        value={formData.box}
                        id="box"
                        label={t("Box")}
                        type="text"
                        isDisabled={true}
                      />
                    </div>

                    <div className="col-sm-12 col-md-4">
                      <LabelWithInputField
                        isMandatory={false}
                        name="zipCode"
                        handleChange={() => { }}
                        value={formData.zipCode}
                        id="zipCode"
                        label={t("Zip code")}
                        type="text"
                        isDisabled={true}
                      />
                    </div>
                    <div className="col-sm-12 col-md-4">
                      <SelectWithSearch
                        title={t("City")}
                        isMandatory={false}
                        search={true}
                        options={[]}
                        onChange={() => { }}
                        isMulti={false}
                        name="city"
                        value={formData.city}
                        isDisabled={true}
                      />
                    </div>
                    <div className="col-sm-12 col-md-4">
                      <SelectWithSearch
                        title={t("Country")}
                        isMandatory={false}
                        search={true}
                        options={[]}
                        onChange={() => { }}
                        isMulti={false}
                        name="country"
                        value={formData.country}
                        isDisabled={true}
                        isTranslate={true}
                      />
                    </div>
                    <div className="col-12">
                      <LabelWithTextAreaField
                        name="motivation"
                        handleChange={() => { }}
                        label={t("Motivation")}
                        isMandatory={false}
                        value={formData.motivation}
                        isDisabled={true}
                      />
                    </div>
                    <div className="col-sm-12 col-md-9">
                      <LabelWithInputField
                        isMandatory={false}
                        name="externalRef"
                        handleChange={handleFieldChange}
                        value={formData.externalRef}
                        id="externalRef"
                        label={t("External reference")}
                        type="text"
                        isDisabled={false}
                      />
                    </div>
                    */}
                    {/* CR: "Candidate resume :" label + clickable resume icon, shown inline
                        where the External reference field used to be. Only rendered when a
                        resume exists. */}
                    {formData.cvFilePath !== "" &&
                      formData.cvFilePath !== null && (
                        <div className="col-sm-12 d-flex align-items-center mb-3">
                          <LabelField
                            title={`${t("Candidate resume")} :`}
                            className="form-label field-label mb-0 me-2"
                          />
                          <Link
                            to={formData.cvFilePath}
                            className="table-action-icons d-inline-flex align-items-center"
                            title={t("View resume")}
                            target="_blank"
                          >
                            <ResumeIcon />
                          </Link>
                        </div>
                      )}
                    {/* OLD standalone resume icon block — replaced by the inline row above.
                    {formData.cvFilePath !== "" &&
                      formData.cvFilePath !== null && (
                        <div className="col-md-3 table-action-icons align-self-center">
                          <Link
                            to={formData.cvFilePath}
                            className="btn p-0 border-0 me-2 mt-2"
                            title={t("View resume")}
                            target="_blank"
                          >
                            <ResumeIcon />
                          </Link>
                        </div>
                      )}
                    */}
                    <div className="col-sm-12 jobExternalInforWrapper">
                      <LabelWithTextAreaField
                        name="externalInfo"
                        handleChange={handleFieldChange}
                        label={t("External info")}
                        isMandatory={false}
                        value={formData.externalInfo}
                        isDisabled={false}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="bg-white p-4 rounded-5 h-100">
                    <div className="tab-subtitle mb-4">
                      {formData.vacancyName}
                    </div>
                    <div className="tab-subtitle mb-3 pt-3">
                      {t("Job description")}
                    </div>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formData.jobDescription,
                      }}
                    />
                    <div className="tab-subtitle mb-3 mt-4 pt-3">
                      {t("Offer")}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: formData.offer }} />
                    <div className="tab-subtitle mb-3 pt-3">{t("Profile")}</div>
                    <div
                      dangerouslySetInnerHTML={{ __html: formData.profile }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row backPadding">
          <div className="col-6 align-self-center">
            <Link
              to="/manage/job-applications"
              className=" back-btn text-decoration-underline"
            >
              {t("Back")}
            </Link>
          </div>
        </div>
      </form>
    </AccessControl>
  );
};

export default ViewJobApplicationOverView;
